/**
 * Full-width banner shown when the app is offline.
 */
import { CloudOff, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { useOfflineStatus } from '../OfflineStatusContext';

export function OfflineBanner() {
  const { t } = useTranslation('common');
  const { hasConnectivity, syncStatus, forceSync, lastSyncedAt } = useOfflineStatus();

  const handleReconnect = async () => {
    await forceSync();
  };

  const formatLastSync = (syncedAt: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - syncedAt.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return t('offline.justNow', 'just now');
    } else if (diffMins < 60) {
      return t('offline.minutesAgo', '{{count}} minutes ago', { count: diffMins });
    } else if (diffHours < 24) {
      return t('offline.hoursAgo', '{{count}} hours ago', { count: diffHours });
    } else {
      return t('offline.daysAgo', '{{count}} days ago', { count: diffDays });
    }
  };

  if (hasConnectivity) {
    return null;
  }

  return (
    <Alert variant="warning" className="rounded-none border-x-0 border-t-0">
      <CloudOff className="h-4 w-4" />
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div>
          <AlertTitle>{t('offline.youreOffline', "You're offline")}</AlertTitle>
          <AlertDescription>
            {lastSyncedAt
              ? t(
                  'offline.viewingCachedData',
                  'Viewing cached data from {{time}}. Some features may be limited.',
                  { time: formatLastSync(lastSyncedAt) }
                )
              : t(
                  'offline.noOfflineData',
                  'You are offline and no data is synced for offline viewing. Check your internet connection.'
                )}
          </AlertDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReconnect}
          disabled={syncStatus === 'syncing'}
          className="shrink-0"
        >
          <RefreshCw className={cn('mr-2 h-4 w-4', syncStatus === 'syncing' && 'animate-spin')} />
          {t('offline.tryReconnecting', 'Try reconnecting')}
        </Button>
      </div>
    </Alert>
  );
}

export default OfflineBanner;
